import { FacilityFilters } from '@reliance/types/filters/FacilityFilters';
import { Facility } from '@reliance/types/models/Facility';
import { useMemo } from 'react';
import { useAsync } from 'react-async-hook';
import api from '../common/api';

export const useFacilities = (filters?: Partial<FacilityFilters>) => {
  const activeOnly = filters?.active === true;
  return useAsync<Facility[]>(
    () =>
      api
        .get<Facility[]>(activeOnly ? '/facility' : '/facility?active=false')
        .then((res) => res.data),
    [activeOnly]
  );
};

export const useFacilityById = (facilityId: number | string | undefined) => {
  const resolvedFacilityId = Number(facilityId);

  return useAsync<Facility | null>(
    async () => {
      if (!Number.isFinite(resolvedFacilityId) || resolvedFacilityId <= 0) {
        return null;
      }
      const res = await api.get<Facility>(`/facility/${resolvedFacilityId}?active=false`);
      return res.data ?? null;
    },
    [resolvedFacilityId]
  );
};

type FacilityExecuteProps = {
  force?: boolean;
};

const cacheKey = 'cachedFacilities';
export const useFacilitiesWithCache = () => {
  const cachedFacilities = useMemo(() => {
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        let parsed = JSON.parse(cachedData) as { expires: number, data: Facility[] };
        if (parsed.expires < Date.now()) {
          localStorage.removeItem(cacheKey);
          return null;
        }
        return parsed.data;
      } catch (error) {
        console.error('Error parsing cached facilities:', error);
      }
    }
    return null;
  }, []);

  const { execute, loading, error, result } = useAsync<Facility[]>(
    (props?: FacilityExecuteProps) => {
      if (cachedFacilities && props.force !== true) {
        return Promise.resolve(cachedFacilities);
      }
      return api.get<Facility[]>('/facility?active=false')
        .then((res) => res.data)
        .then((res) => {
          localStorage.setItem(cacheKey, JSON.stringify({
            data: res,
            expires: Date.now() + (60 * 15 * 1000) // 15min
          }));
          return res;
        });
    },
    null
  );

  return {
    loading,
    error,
    execute: (props?: FacilityExecuteProps) => execute(props),
    result: cachedFacilities || result || [],
  };
};

export const clearFacilityCache = () => localStorage.removeItem(cacheKey);